import { create } from 'zustand';
import { adminSyncBatchesApi } from '@api/api-config.ts';
import { GetAdminSyncBatchListSyncTypeEnum } from '@api/generated/portal-be-admin';
import {
  PaginationInfo,
  SyncBatchContentSummary,
  SyncBatchDbCheck,
  SyncBatchListItem,
  SyncBatchPriceIssue,
  SyncBatchPriceRow,
  SyncBatchPriceSortBy,
  SyncBatchSortDirection,
  SyncBatchStatus,
} from './types.ts';

export interface SyncBatchListFilters {
  syncType?: GetAdminSyncBatchListSyncTypeEnum;
  status?: SyncBatchStatus;
  author?: string;
  onlyWithPrices?: boolean;
  requestId?: string;
  page: number;
  size: number;
}

export interface SyncBatchPriceFilters {
  requestId: string;
  product?: string;
  supplier?: string;
  client?: string;
  portalSourceId?: number;
  priceFrom?: number;
  priceTo?: number;
  issue?: SyncBatchPriceIssue;
  sortBy?: SyncBatchPriceSortBy;
  sortDir?: SyncBatchSortDirection;
  page: number;
  size: number;
}

const toPagination = (r: {
  page: number;
  size: number;
  total: number;
  totalPages: number;
  hasPrev: boolean;
  hasNext: boolean;
}): PaginationInfo => ({
  page: r.page,
  size: r.size,
  total: r.total,
  totalPages: r.totalPages,
  hasPrev: r.hasPrev,
  hasNext: r.hasNext,
});

interface AdminSyncBatchStoreState {
  // přehled dávek
  listLoading: boolean;
  listError: string | null;
  batches: SyncBatchListItem[] | null;
  listPagination: PaginationInfo | null;
  loadBatches: (filters: SyncBatchListFilters) => Promise<void>;

  // detail dávky
  summary: SyncBatchContentSummary | null;
  summaryLoading: boolean;
  pricesLoading: boolean;
  detailError: string | null;
  prices: SyncBatchPriceRow[] | null;
  pricePagination: PaginationInfo | null;
  loadBatchDetail: (filters: SyncBatchPriceFilters) => Promise<void>;

  // ověření proti DB — na vyžádání, prochází celou dávku
  dbCheck: SyncBatchDbCheck | null;
  dbCheckLoading: boolean;
  dbCheckError: string | null;
  loadDbCheck: (requestId: string) => Promise<void>;
}

export const useAdminSyncBatchStore = create<AdminSyncBatchStoreState>(set => ({
  listLoading: false,
  listError: null,
  batches: null,
  listPagination: null,

  summary: null,
  summaryLoading: false,
  pricesLoading: false,
  detailError: null,
  prices: null,
  pricePagination: null,

  dbCheck: null,
  dbCheckLoading: false,
  dbCheckError: null,

  loadBatches: async (filters: SyncBatchListFilters) => {
    try {
      set({ listLoading: true, listError: null });
      const { data } = await adminSyncBatchesApi.getAdminSyncBatchList({
        page: filters.page,
        size: filters.size,
        syncType: filters.syncType || undefined,
        status: filters.status || undefined,
        author: filters.author?.trim() ? filters.author.trim() : undefined,
        onlyWithPrices: filters.onlyWithPrices || undefined,
        requestId: filters.requestId?.trim() ? filters.requestId.trim() : undefined,
      });
      set({
        listLoading: false,
        listError: null,
        batches: (data.items || []) as unknown as SyncBatchListItem[],
        listPagination: toPagination(data),
      });
    } catch (error) {
      set({ listLoading: false, listError: error instanceof Error ? error.message : 'Chyba při načítání dávek' });
    }
  },

  /**
   * Souhrn a řádky se tahají paralelně, ale souhrn se přepočítává jen při změně dávky —
   * je to celý rozbalený payload, takže ho filtrování stránky nemá proč spouštět znovu.
   */
  loadBatchDetail: async (filters: SyncBatchPriceFilters) => {
    const isSameBatch = useAdminSyncBatchStore.getState().summary?.requestId === filters.requestId;
    try {
      set({ pricesLoading: true, summaryLoading: !isSameBatch, detailError: null });

      const pricesPromise = adminSyncBatchesApi.getAdminSyncBatchPrices({
        requestId: filters.requestId,
        page: filters.page,
        size: filters.size,
        product: filters.product?.trim() ? filters.product.trim() : undefined,
        supplier: filters.supplier?.trim() ? filters.supplier.trim() : undefined,
        client: filters.client?.trim() ? filters.client.trim() : undefined,
        portalSourceId: filters.portalSourceId,
        priceFrom: filters.priceFrom,
        priceTo: filters.priceTo,
        issue: filters.issue,
        sortBy: filters.sortBy,
        sortDir: filters.sortDir,
      });

      const summaryPromise = isSameBatch
        ? Promise.resolve(null)
        : adminSyncBatchesApi.getAdminSyncBatchContentSummary({ requestId: filters.requestId });

      const [pricesResp, summaryResp] = await Promise.all([pricesPromise, summaryPromise]);

      set(state => ({
        pricesLoading: false,
        summaryLoading: false,
        detailError: null,
        prices: (pricesResp.data.items || []) as unknown as SyncBatchPriceRow[],
        pricePagination: toPagination(pricesResp.data),
        summary: summaryResp ? (summaryResp.data as unknown as SyncBatchContentSummary) : state.summary,
        // Výsledek ověření platí pro jednu dávku — při přechodu na jinou se musí zahodit,
        // jinak by u nové dávky svítila cizí čísla.
        dbCheck: isSameBatch ? state.dbCheck : null,
        dbCheckError: isSameBatch ? state.dbCheckError : null,
      }));
    } catch (error) {
      set({
        pricesLoading: false,
        summaryLoading: false,
        detailError: error instanceof Error ? error.message : 'Chyba při načítání obsahu dávky',
      });
    }
  },

  loadDbCheck: async (requestId: string) => {
    try {
      set({ dbCheckLoading: true, dbCheckError: null });
      const { data } = await adminSyncBatchesApi.getAdminSyncBatchDbCheck({ requestId });
      set({ dbCheckLoading: false, dbCheck: data as unknown as SyncBatchDbCheck });
    } catch (error) {
      set({
        dbCheckLoading: false,
        dbCheckError: error instanceof Error ? error.message : 'Chyba při ověřování dávky proti DB',
      });
    }
  },
}));
